import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { UtilFns } from "./util-fns";

/**
 * Reusable scanner input component for 2D wedge/remote barcode scanners.
 *
 * Features:
 * - Auto-focus on mount
 * - Detects complete scan when input reaches maxLength
 * - Executes search callback and emits result
 * - Auto-clears and refocuses after scan
 * - Shows error message on not found
 * - Disables when parent is busy
 * - Dynamic label based on state
 */
@Component({
  selector: "uni-scanner-input",
  templateUrl: "./scanner-input.component.html",
  standalone: false,
})
export class ScannerInputComponent<T> implements AfterViewInit {
  @ViewChild("scanInput") scanInput!: ElementRef<HTMLInputElement>;

  /** Default label text (e.g., "Scan Item") */
  @Input() label = "Scan...";

  /** Label shown when disabled/busy (e.g., "Please wait...") */
  @Input() busyLabel = "Please wait...";

  /** CSS class to apply to the label (e.g., "font-bold text-lg") */
  @Input() labelClass = "";

  /** Disables the input when parent is busy */
  @Input() disabled = false;

  /** Callback to search for scanned value. Returns matched item or null. */
  @Input() searchFn!: (value: string) => Promise<T | null>;

  /** Expected barcode length (default: 12) */
  @Input() maxLength = 12;

  /** Error message to display when search fails (default: "* Not found *") */
  @Input() notFoundMessage = "* Not found *";

  /** Emits the matched item on successful scan */
  @Output() scanResult = new EventEmitter<T>();

  /** Emits error message on failed scan */
  @Output() scanError = new EventEmitter<string>();

  /** Current scanned value (bound to input) */
  scannedValue = "";

  /** Error message displayed below input */
  errorMessage = "";

  /** Internal busy state during search */
  isSearching = false;

  ngAfterViewInit() {
    this.focus();
  }

  /** Get the current label based on state */
  get currentLabel(): string {
    return this.disabled || this.isSearching ? this.busyLabel : this.label;
  }

  /** Focus the input element */
  focus() {
    this.scanInput?.nativeElement?.focus();
  }

  /** Handle input event - triggers search when maxLength reached */
  async onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.value.length >= this.maxLength) {
      await this.performSearch(input.value);
    }
  }

  /** Execute the search callback and handle result */
  private async performSearch(value: string) {
    if (!this.searchFn) {
      console.warn("ScannerInputComponent: searchFn not provided");
      return;
    }

    this.errorMessage = "";
    this.isSearching = true;

    try {
      const result = await this.searchFn(value);

      if (result) {
        this.scanResult.emit(result);
        this.errorMessage = "";
      } else {
        this.errorMessage = this.notFoundMessage;
        this.scanError.emit(this.errorMessage);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Search failed";
      if (errorMsg) {
        this.errorMessage = `* ${errorMsg} *`;
        this.scanError.emit(this.errorMessage);
      }
    } finally {
      this.isSearching = false;

      // Always clear input and refocus for next scan
      this.scannedValue = "";
      await UtilFns.wait(1);
      this.focus();
    }
  }

  /** Clear the input and error message */
  clear() {
    this.scannedValue = "";
    this.errorMessage = "";
  }
}
